/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 global header: logo, edition, search (⌘K), Ask Seep, New, messages,
 * settings, notifications and the avatar menu. Every label and icon is white
 * on the navy-to-blue gradient; the gold New button carries navy text.
 *
 * Edition switching arrives in Phase 4; until then the label follows the
 * tenant (Travel when the travel edition is on, otherwise Sales CRM) and
 * there is no dropdown.
 */

import { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { formatDistanceToNow } from "date-fns";
import {
  Bell,
  Check,
  ChevronDown,
  GraduationCap,
  Headset,
  HeartPulse,
  House,
  CheckCheck,
  LifeBuoy,
  LogOut,
  Menu as MenuIcon,
  MessageSquare,
  Phone,
  Plane,
  Plus,
  Search,
  Settings,
  Sparkles,
  Target,
  Undo2,
  User,
} from "lucide-react";
import { useAuth } from "auth/Auth";
import { useAiAssistant } from "context/AiAssistantContext";
import { useTravelUi } from "hooks/useTravelUi";
import useUiVersion from "hooks/useUiVersion";
import { UI_LEGACY } from "hooks/uiVersion";
import { getNotificationRedirectPath } from "utils/notificationRedirect";
import { parseServerDateTime } from "utils/DateTimeConversion";
import pearlLogo from "assets/images/pearl-logo.png";
import pearlLogoDark from "assets/images/pearl-crm-logo-dark.png";
import { V2_HEADER_GRADIENT, v2 as t, v2EditionAccents } from "styles/tokens/v2";
import { useNotifications, useWhatsappUnread } from "./useHeaderData";
import { useActiveApp } from "./apps";

// app_registry.py names its icons; only these are used there.
const APP_ICONS = { Target, Headset, Plane, House, GraduationCap, HeartPulse };

export const HEADER_HEIGHT = 56;

const iconBtnSx = {
  width: 36,
  height: 36,
  color: "#FFFFFF",
  borderRadius: "8px",
  "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
  "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: 1 },
};

function initials(user) {
  const base = (user?.username || "").split("@")[0];
  const parts = base.split(".").filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

function NotificationsButton() {
  const navigate = useNavigate();
  const { notifications, unread, loading, error, markRead, markAllRead } = useNotifications();
  const [anchor, setAnchor] = useState(null);

  const openRecord = async (n) => {
    setAnchor(null);
    try {
      if (!n.is_read) await markRead(n.id);
    } catch {
      // Opening the record matters more than the read flag.
    }
    const path = getNotificationRedirectPath(n.parent_table, n.parent_table_id);
    if (path) navigate(path);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton aria-label={`Notifications, ${unread} unread`} sx={iconBtnSx} onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge variant="dot" invisible={!unread} sx={{ "& .MuiBadge-dot": { bgcolor: t.gold, boxShadow: `0 0 0 2px ${t.primary}` } }}>
            <Bell size={20} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 380, maxHeight: 480 } }}
      >
        <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.text }}>Notifications</Typography>
          <ButtonBase
            disabled={!unread}
            onClick={() => markAllRead().catch(() => {})}
            sx={{ fontSize: 13, color: unread ? t.link : t.textDisabled, gap: 0.5, fontFamily: "inherit" }}
          >
            <CheckCheck size={15} /> Mark all read
          </ButtonBase>
        </Box>
        <Divider />
        {error && <Typography sx={{ px: 2, py: 2, fontSize: 13, color: "#B42318" }}>{error}</Typography>}
        {!error && !loading && notifications.length === 0 && (
          <Typography sx={{ px: 2, py: 3, fontSize: 13, color: t.text2, textAlign: "center" }}>No notifications</Typography>
        )}
        {notifications.map((n) => {
          const when = parseServerDateTime(n.created_at);
          return (
            <MenuItem key={n.id} onClick={() => openRecord(n)} sx={{ alignItems: "flex-start", gap: 1.5, py: 1.25, whiteSpace: "normal", bgcolor: n.is_read ? "transparent" : "rgba(21,101,200,0.05)" }}>
              <Box sx={{ width: 8, height: 8, mt: 0.75, borderRadius: "50%", flexShrink: 0, bgcolor: n.is_read ? "transparent" : t.primary }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.text }}>{n.title}</Typography>
                <Typography sx={{ fontSize: 13, color: t.text2 }}>{n.message}</Typography>
                <Typography sx={{ fontSize: 12, color: t.text3, mt: 0.25 }}>
                  {[n.owner?.name, when ? formatDistanceToNow(when, { addSuffix: true }) : null].filter(Boolean).join(" · ")}
                </Typography>
              </Box>
              {!n.is_read && (
                <Tooltip title="Mark as read">
                  <IconButton
                    size="small"
                    aria-label="Mark as read"
                    onClick={(e) => {
                      e.stopPropagation();
                      markRead(n.id).catch(() => {});
                    }}
                  >
                    <Check size={15} />
                  </IconButton>
                </Tooltip>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

function AvatarMenu({ softphone }) {
  const navigate = useNavigate();
  const { user, signout } = useAuth();
  const { setUiVersion, locked: uiLocked } = useUiVersion();
  const [anchor, setAnchor] = useState(null);
  const go = (path) => {
    setAnchor(null);
    navigate(path);
  };
  return (
    <>
      <Tooltip title={user?.username || "Account"}>
        <ButtonBase
          aria-label="Account menu"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ width: 34, height: 34, ml: 0.5, borderRadius: "50%", bgcolor: t.header, color: "#FFFFFF", border: "2px solid rgba(255,255,255,0.85)", fontSize: 13, fontWeight: 700, fontFamily: "inherit", overflow: "hidden" }}
        >
          {/* Uploaded profile picture, else the initials (also if it fails to load). */}
          <Avatar
            src={user?.avatar || undefined}
            alt=""
            sx={{ width: "100%", height: "100%", bgcolor: "transparent", color: "inherit", fontSize: "inherit", fontWeight: "inherit", fontFamily: "inherit" }}
          >
            {initials(user)}
          </Avatar>
        </ButtonBase>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 240 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.text }} noWrap>{user?.username}</Typography>
          {user?.email && <Typography sx={{ fontSize: 12, color: t.text2 }} noWrap>{user.email}</Typography>}
        </Box>
        <Divider />
        <MenuItem onClick={() => go("/profile")}>
          <ListItemIcon><User size={16} /></ListItemIcon>Profile settings
        </MenuItem>
        <MenuItem onClick={() => go("/support-ticket")}>
          <ListItemIcon><LifeBuoy size={16} /></ListItemIcon>Contact support
        </MenuItem>
        {softphone?.canHide && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              softphone.setHidden(!softphone.hiddenByUser);
            }}
          >
            <ListItemIcon><Phone size={16} /></ListItemIcon>
            {softphone.hiddenByUser ? "Show the calling bar" : "Hide the calling bar"}
          </MenuItem>
        )}
        {!uiLocked && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setUiVersion(UI_LEGACY).catch(() => {});
            }}
          >
            <ListItemIcon><Undo2 size={16} /></ListItemIcon>Switch back to classic
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            signout();
          }}
          sx={{ color: "#B42318" }}
        >
          <ListItemIcon sx={{ color: "inherit" }}><LogOut size={16} /></ListItemIcon>Sign out
        </MenuItem>
      </Menu>
    </>
  );
}

AvatarMenu.propTypes = {
  softphone: PropTypes.shape({ canHide: PropTypes.bool, hiddenByUser: PropTypes.bool, setHidden: PropTypes.func }),
};
AvatarMenu.defaultProps = { softphone: null };

function NewMenu({ createRoutes }) {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  if (!createRoutes.length) return null;
  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        sx={{ height: 34, px: 1.5, gap: 0.75, borderRadius: "8px", bgcolor: t.gold, color: t.onGold, fontWeight: 700, fontSize: 14, fontFamily: "inherit", "&:hover": { bgcolor: "#F0A800" } }}
      >
        <Plus size={16} strokeWidth={2.5} /> New
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {createRoutes.map((r) => (
          <MenuItem
            key={r.key}
            onClick={() => {
              setAnchor(null);
              navigate(r.route);
            }}
          >
            {r.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
NewMenu.propTypes = { createRoutes: PropTypes.arrayOf(PropTypes.object).isRequired };

export default function HeaderV2({ onOpenPalette, onOpenNav, narrow, createRoutes, seepInHeader, softphone }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ai = useAiAssistant();
  const { showTravel } = useTravelUi();
  const whatsapp = useWhatsappUnread();
  const { app, apps, setApp } = useActiveApp();
  const [appMenu, setAppMenu] = useState(null);
  // Before the apps arrive (or on a backend without them) fall back to the
  // tenant's travel flag, as Phase 2 did.
  const edition = app
    ? { name: app.name, short: app.short || app.name, Icon: APP_ICONS[app.icon] || Target, accent: app.accent }
    : showTravel
      ? { name: "Travel", short: "Travel", Icon: Plane, accent: v2EditionAccents.travel.accent }
      : { name: "Sales CRM", short: "Sales CRM", Icon: Target, accent: v2EditionAccents.sales.accent };
  const canSwitch = Array.isArray(apps) && apps.length >= 2;
  const switchTo = (next) => {
    setAppMenu(null);
    if (!next || next.code === app?.code) return;
    setApp(next.code);
    navigate(next.home || "/dashboard");
  };
  const isAdmin = user?.role === "system_admin";

  return (
    <Box
      component="header"
      sx={{
        height: HEADER_HEIGHT,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        // Phone widths: tighter spacing so every control fits in 360px.
        gap: narrow ? 0.5 : 1.25,
        px: narrow ? 1 : 1.75,
        background: V2_HEADER_GRADIENT,
        color: "#FFFFFF",
        zIndex: 40,
      }}
    >
      {narrow && (
        <IconButton aria-label="Open menu" onClick={onOpenNav} sx={iconBtnSx}>
          <MenuIcon size={20} />
        </IconButton>
      )}
      <ButtonBase onClick={() => navigate("/dashboard")} aria-label="Pearl CRM home" sx={{ gap: 1, borderRadius: "8px", fontFamily: "inherit" }}>
        {/* The brand's dark-background lockup; narrow screens keep the mark only. */}
        {narrow ? (
          <Box component="img" src={pearlLogo} alt="" sx={{ height: 30, width: 30, objectFit: "contain" }} />
        ) : (
          <Box component="img" src={pearlLogoDark} alt="" sx={{ height: 32, width: "auto", display: "block" }} />
        )}
      </ButtonBase>
      {!narrow && <Box sx={{ width: "1px", height: 24, bgcolor: "rgba(255,255,255,0.22)", mx: 0.5 }} />}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: "7px", bgcolor: edition.accent, border: "1px solid rgba(255,255,255,0.35)", display: "grid", placeItems: "center" }}>
          <edition.Icon size={16} color="#FFFFFF" aria-hidden="true" />
        </Box>
        {/* Narrow screens show the app's tile only; the switcher still names it. */}
        {!narrow && (
          <Typography component="span" sx={{ fontSize: 15, fontWeight: 600, color: "#FFFFFF" }} noWrap>
            {edition.name}
          </Typography>
        )}
        {/* Only a dropdown when there is something to switch to. */}
        {canSwitch && (
          <>
            <IconButton
              aria-label={`Switch app, current: ${edition.name}`}
              aria-haspopup="menu"
              onClick={(e) => setAppMenu(e.currentTarget)}
              sx={{ ...iconBtnSx, width: 28, height: 28 }}
            >
              <ChevronDown size={16} />
            </IconButton>
            <Menu
              anchorEl={appMenu}
              open={Boolean(appMenu)}
              onClose={() => setAppMenu(null)}
              PaperProps={{ sx: { width: 260 } }}
            >
              <Typography sx={{ px: 2, pt: 0.5, pb: 1, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: t.text3 }}>
                Your apps
              </Typography>
              {apps.map((a) => {
                const AppIcon = APP_ICONS[a.icon] || Target;
                return (
                  <MenuItem key={a.code} selected={a.code === app?.code} onClick={() => switchTo(a)} sx={{ gap: 1.25 }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: "7px", bgcolor: a.accent, display: "grid", placeItems: "center", flexShrink: 0 }}>
                      <AppIcon size={15} color="#FFFFFF" aria-hidden="true" />
                    </Box>
                    {a.name}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        )}
      </Box>

      <Box sx={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>
        {narrow ? null : (
          <ButtonBase
            onClick={onOpenPalette}
            aria-label="Search or jump to (Command K)"
            sx={{
              width: "100%",
              maxWidth: 520,
              height: 36,
              px: 1.5,
              gap: 1,
              justifyContent: "flex-start",
              borderRadius: "10px",
              border: "1px solid rgba(255,255,255,0.26)",
              bgcolor: "rgba(255,255,255,0.12)",
              color: "#FFFFFF",
              fontFamily: "inherit",
              fontSize: 14,
              "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
            }}
          >
            <Search size={17} aria-hidden="true" />
            <Box component="span" sx={{ flex: 1, textAlign: "left", opacity: 0.9 }}>Search {edition.short} or jump to anything</Box>
            <Box component="kbd" sx={{ fontSize: 11, border: "1px solid rgba(255,255,255,0.4)", borderRadius: "4px", px: 0.75, fontFamily: "inherit" }}>⌘K</Box>
          </ButtonBase>
        )}
      </Box>

      {narrow && (
        <IconButton aria-label="Search" onClick={onOpenPalette} sx={iconBtnSx}>
          <Search size={20} />
        </IconButton>
      )}
      {/* While the calling bar is showing, Ask Seep lives in it instead. */}
      {ai.enabled && seepInHeader && (
        <ButtonBase
          onClick={ai.toggle}
          aria-pressed={ai.open}
          sx={{ height: 34, px: 1.5, gap: 0.75, borderRadius: "17px", border: "1px solid rgba(255,212,92,0.75)", bgcolor: ai.open ? "rgba(255,180,0,0.30)" : "rgba(255,255,255,0.10)", color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "inherit" }}
        >
          <Sparkles size={16} color={t.gold} aria-hidden="true" /> {!narrow && "Ask Seep"}
        </ButtonBase>
      )}
      {!narrow && <NewMenu createRoutes={createRoutes} />}
      {whatsapp.enabled && (
        <Tooltip title="Messages">
          <IconButton aria-label={`Messages, ${whatsapp.count} unread`} sx={iconBtnSx} onClick={() => navigate("/messages")}>
            <Badge badgeContent={whatsapp.count} max={99} color="error" invisible={!whatsapp.count}>
              <MessageSquare size={20} />
            </Badge>
          </IconButton>
        </Tooltip>
      )}
      {isAdmin && (
        <Tooltip title="Settings">
          <IconButton aria-label="Settings" sx={iconBtnSx} onClick={() => navigate("/settings")}>
            <Settings size={20} />
          </IconButton>
        </Tooltip>
      )}
      <NotificationsButton />
      <AvatarMenu softphone={softphone} />
    </Box>
  );
}

HeaderV2.propTypes = {
  onOpenPalette: PropTypes.func.isRequired,
  onOpenNav: PropTypes.func.isRequired,
  narrow: PropTypes.bool.isRequired,
  createRoutes: PropTypes.arrayOf(PropTypes.object).isRequired,
  seepInHeader: PropTypes.bool,
  softphone: PropTypes.shape({ canHide: PropTypes.bool, hiddenByUser: PropTypes.bool, setHidden: PropTypes.func }),
};
HeaderV2.defaultProps = { seepInHeader: true, softphone: null };
